'use client'
import { useState } from 'react'
import { VStack } from '@chakra-ui/react'
import type { CreateInvoiceOrderDTO, OrderShippingAdditionalDataDTO, ShipmentDTO } from 'ecosystem'
import { useBuyerShipping } from '../../../../common/hooks'
import { useCheckoutContext } from '../../../hooks'
import { PostNordCheckout } from '../../PostnordCheckout'
import { BillingAddressPicker, useBillingAddress } from '../DefaultDelivery'
import { PaymentWithDelivery } from '../PaymentWithDelivery'
import { InvoiceReviewPanel } from './InvoiceReviewPanel'
import { useDebouncedDraftSync } from './useDebouncedDraftSync'

interface InvoiceAndPostnordProps {
  submitPath: string
}

export const InvoiceAndPostnord = ({ submitPath }: InvoiceAndPostnordProps) => {
  const { summaryTotal, invoiceOrderResponse, paymentProviderConfig } = useCheckoutContext()
  const { rebaseShippingQuote } = useBuyerShipping(paymentProviderConfig)
  const [shippingCost, setShippingCost] = useState<number | undefined>(undefined)
  const [postnordDetails, setPostnordDetails] = useState<
    OrderShippingAdditionalDataDTO['postnordDetails'] | undefined
  >(undefined)
  const [isValid, setIsValid] = useState(false)
  const { billingAddress, setBillingAddress } = useBillingAddress(
    invoiceOrderResponse?.storefrontOrderResponse?.billingAddress
  )

  const shipment: ShipmentDTO = {
    shippingProvider: 'POSTNORD',
    ...(postnordDetails
      ? {
          shippingType: postnordDetails.isPickupFromStore ? 'PICKUP' : 'DELIVERY',
          additionalInfo: { postnordDetails }
        }
      : {})
  }

  const draftOverrides: Partial<CreateInvoiceOrderDTO> = {
    ...(shippingCost !== undefined ? { shippingCost } : {}),
    shipments: [shipment],
    ...(billingAddress ? { billingAddress } : {})
  }

  const { flush: flushDraftSync } = useDebouncedDraftSync(draftOverrides, isValid)

  return (
    <PaymentWithDelivery
      deliveryComponent={
        <PostNordCheckout
          cartTotalAmount={summaryTotal}
          onDataChange={(data, meta) => {
            if (meta.deliveryOptionChanged || meta.totalPriceChanged) {
              setShippingCost(rebaseShippingQuote(data.totalPrice))
            }
            setPostnordDetails({
              isPickupFromStore: data.isPickupFromStore,
              selectedPickup: data.pickupFromStore,
              recipientAddress: data.recipientAddress,
              deliveryOption: data.deliveryOption
            })
            setIsValid(meta.isValid)
          }}
        />
      }
      paymentComponent={
        <VStack align="stretch" gap={6} maxW="600px" w="100%">
          <BillingAddressPicker onChange={setBillingAddress} value={billingAddress} />
          <InvoiceReviewPanel
            draftOverrides={draftOverrides}
            flushDraftSync={flushDraftSync}
            isDisabled={!isValid}
            submitPath={submitPath}
          />
        </VStack>
      }
    />
  )
}
